import React from 'react';
import { simpleDateNoTime } from 'app/utils/parsers';
import history from 'app/routes/history';
import Button from 'app/components/general/Button';
import SectionGroup from 'app/components/general/SectionGroup';
import ModalConfirm from 'app/components/general/ModalConfirm';

interface IStep4ConfirmProps {
  isValid: Function;
  handleChange: Function;
  handleSubmit: Function;
  formData: any;
  actions: any;
  payeeType: string;
  errors: any;
  appReady: boolean;
  changed: boolean;
  paymentRequests: any;
  originalFormData: any;
  selectedRowIds: string[];
}

interface IStep4ConfirmState {
  submitModalOpen: boolean;
}

export default class Step4Confirm extends React.Component<IStep4ConfirmProps, IStep4ConfirmState> {
  static defaultProps: IStep4ConfirmProps = {
    actions: undefined,
    formData: undefined,
    payeeType: '',
    appReady: false,
    changed: false,
    paymentRequests: undefined,
    originalFormData: undefined,
    isValid: undefined,
    handleChange: undefined,
    handleSubmit: undefined,
    errors: undefined,
    selectedRowIds: [],
  };

  state = {
    submitModalOpen: false,
  }

  handleBack = () => {
    history.push('/payments/segments/requests/select');
  };

  handleOpenSubmitModal = (): void => {
    this.setState({ submitModalOpen: true });
  }

  handleCloseSubmitModal = (): void => {
    this.setState({ submitModalOpen: false });
  }

  parseSelectedPayeeText = (number, type): string => {
    const parsedNumber = number > 0 ? number : 0;
    const parsedType = type === 'individuals' ? 'individual' : 'team';
    const parsedText = `${parsedNumber.toString()} ${parsedType}`;
    const finalText = number !== 1 ? `${parsedText}s` : parsedText;
    return finalText;
  }

  parseInitialNotificationText = (sendEmailNotification, sendSMSNotification): string => {
    if (sendEmailNotification && sendSMSNotification) {
      return 'Email, SMS';
    } if (sendEmailNotification && !sendSMSNotification) {
      return 'Email';
    } if (!sendEmailNotification && sendSMSNotification) {
      return 'SMS';
    }
    return '';
  }

  parseReminderText = (sendEmailReminders, sendSMSReminders): string => {
    if (sendEmailReminders && sendSMSReminders) {
      return 'Yes (Email, SMS)';
    } if (sendEmailReminders && !sendSMSReminders) {
      return 'Yes (Email)';
    } if (!sendEmailReminders && sendSMSReminders) {
      return 'Yes (SMS)';
    }
    return 'No';
  }

  renderSubmitModal = (): React.ReactNode => (
    <ModalConfirm
      title="Send payment request"
      description="This will send out notifications to all selected payees immediately."
      buttonText="Send"
      isOpen={this.state.submitModalOpen}
      isLoading={this.props.paymentRequests.isSaving}
      handleSubmit={this.props.handleSubmit}
      handleCancel={this.handleCloseSubmitModal}
    />
  );

  render(): React.ReactNode {
    const selectedPayeeText = this.parseSelectedPayeeText(this.props.selectedRowIds.length, this.props.payeeType);
    return (
      <div>
        <div className="row">
          <div className="col">
            {this.props.children}
          </div>
        </div>
        <div className="row with-margin-top">
          <div className="col">
            <SectionGroup title="Confirm Payment Request">
              <div className="row">
                <div className="col col-4">
                  Payment Type
                </div>
                <div className="col text-weight-medium-bold">
                  {this.props.formData.paymentType?.name}
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col col-4">
                  Payment Description
                </div>
                <div className="col text-weight-medium-bold">
                  {this.props.formData.description}
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col col-4">
                  Amount
                </div>
                <div className="col text-weight-medium-bold">
                  {this.props.formData.amount}
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col col-4">
                  Due Date
                </div>
                <div className="col text-weight-medium-bold">
                  {this.props.formData.dueDate !== undefined ? simpleDateNoTime(this.props.formData.dueDate[0]) : ''}
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col">
                  <div className="divider full" />
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col col-4">
                  Payee/s
                </div>
                <div className="col text-weight-medium-bold">
                  {_.capitalize(this.props.payeeType)}
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col col-4">
                  Selected Payee/s
                </div>
                <div className="col text-weight-medium-bold">
                  {selectedPayeeText}
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col">
                  <div className="divider full" />
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col col-4">
                  Initial Notification
                </div>
                <div className="col text-weight-medium-bold">
                  {this.parseInitialNotificationText(this.props.formData.sendEmailNotification, this.props.formData.sendSMSNotification)}
                </div>
              </div>
              <div className="row with-margin-top-10">
                <div className="col col-4">
                  Reminders
                </div>
                <div className="col text-weight-medium-bold">
                  {this.parseReminderText(this.props.formData.sendEmailReminders, this.props.formData.sendSMSReminders)}
                </div>
              </div>
              {/* <div className="row with-margin-top-10">
                <div className="col col-4">
                  Reminder Frequency
                </div>
                <div className="col text-weight-medium-bold">
                  {this.props.formData.amount}
                </div>
              </div> */}
            </SectionGroup>
          </div>
        </div>
        {this.renderSubmitModal()}
        <div className="row with-margin-top">
          <div className="col btn-flex-group">
            <div className="btn-flex-container">
              <Button
                className="btn-standard btn-1"
                type="submit"
                noLoading
                isLoading={this.props.paymentRequests.isSaving}
                hasSaved={this.props.paymentRequests.hasSaved}
                hasFailed={this.props.paymentRequests.hasFailed}
                onClick={this.handleOpenSubmitModal}
              >
                Send Payment Request
              </Button>
              <Button onClick={this.handleBack} className="btn-standard btn-2">Back</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
